/**
 * src/utils/uarIdentityEvidence.js
 *
 * Identity module ↔ User Access Review — one access-review story, not two.
 *
 * Tryggr now has two things that answer ISO 27001 A.5.18, "are people's access
 * rights still right", from opposite directions:
 *
 *   UAR campaigns — periodic, human attestation. A reviewer certifies each
 *     employee's access and a "revoke" decision raises an access-revocation
 *     task. This is scheduled judgement.
 *
 *   The identity derivation — continuous, evidence-based. Access a person holds
 *     that no job role of theirs requires.
 *
 * Left unconnected these become two review processes that can disagree about
 * whether somebody's access was reviewed, which is exactly what "Enter Once,
 * Know Everywhere" exists to prevent. This module connects them in one
 * direction only: the derivation FEEDS the campaign.
 *
 * IT NEVER CERTIFIES. A reviewer looking at "three of these four holdings are
 * explained by her job roles, one is not" is doing a materially better review
 * than one reading a bare list — but the decision stays theirs and stays
 * recorded. A campaign where the system pre-answered the questions is not an
 * access review, and reporting one as completed would be the second failure
 * named in TRYGGR_INTENT.md §6.
 *
 * SYSTEM OF RECORD (IDN-B-009): "when was this person's access last reviewed"
 * is answered by the UAR campaign decision and nothing else. The derivation
 * says whether access is explained; it never says it was reviewed, because
 * nobody looked.
 */

/**
 * What a reviewer should see next to one employee, derived from the matrix.
 *
 * @param {object} person a row from buildRoleMatrix().people, or null
 * @returns {{
 *   known: boolean, explained: Array, unexplained: Array, missing: Array,
 *   hasEvidence: boolean, unknownReason: string|null
 * }}
 */
export function identityEvidenceForReviewer(person) {
  if (!person) {
    return {
      known: false,
      explained: [],
      unexplained: [],
      missing: [],
      hasEvidence: false,
      unknownReason:
        'No user account is linked to this employee, so their access cannot be listed.',
    };
  }

  // Everything the person holds, and whether a job role explains it.
  const unexplainedIds = new Set(
    (person.accessFindings || [])
      .filter((f) => f.kind === 'drift' || f.kind === 'revocation-due')
      .map((f) => `${f.system}:${f.id}`)
  );

  const held = [
    ...(person.platformRole
      ? [{ system: 'platform', id: person.platformRole.id, name: person.platformRole.name }]
      : []),
    ...(person.governanceRoles || []).map((g) => ({
      system: 'governance',
      id: g.roleKey,
      name: g.name,
    })),
  ];

  const explained = held.filter((h) => !unexplainedIds.has(`${h.system}:${h.id}`));
  const unexplained = (person.accessFindings || [])
    .filter((f) => f.kind === 'drift' || f.kind === 'revocation-due')
    .map((f) => ({ system: f.system, id: f.id, name: f.name, kind: f.kind, because: f.because }));
  const missing = (person.grantGaps || []).map((f) => ({
    system: f.system,
    id: f.id,
    name: f.name,
    because: f.because,
  }));

  // IDN-B-004 again: a governance store that did not load means the holdings
  // list is incomplete. Saying "one unexplained holding" off a partial list
  // would understate what the reviewer is certifying.
  const unknownReason = person.governanceKnown
    ? null
    : 'Governance roles could not be read, so this list may be incomplete.';

  return {
    known: true,
    explained,
    unexplained,
    missing,
    hasEvidence: held.length > 0 || missing.length > 0,
    unknownReason,
  };
}

/** Stable id for one holding, matching the identity task key's tail so a UAR
 *  revoke decision and an identity revocation task can be correlated. */
export function holdingKey(system, id) {
  return `${system}:${id}`;
}

/**
 * Identity keys an open UAR revoke task already covers for a person, so the
 * identity sweep does not raise a second task for work a reviewer has already
 * called for. Granularity differs — UAR decides per employee, the derivation
 * per access — so the UAR task carries the specific holdings it covers.
 */
export function uarCoveredIdentityKeys(adhocTasks = []) {
  const covered = new Set();
  for (const t of adhocTasks) {
    if (t?.type !== 'access-revocation' && t?.taskType !== 'access-revocation') continue;
    if (t.status === 'closed' || t.status === 'completed') continue;
    for (const key of t.coversIdentityKeys || []) covered.add(key);
  }
  return covered;
}
